/**
 * Dashboard主界面 - 整合会话管理
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SessionList from './SessionList';
import GraphUploader from './GraphUploader';
import InteractiveGraph from './InteractiveGraph';
import ChatInterface from './ChatInterface';
import RealtimeVoiceTutorPanel from './RealtimeVoiceTutorPanel';
import SettingsPanel from './SettingsPanel';
import ProblemIntegrationView from './ProblemIntegrationView';
import FuriganaText from './FuriganaText';
import { useFuriganaAria } from '../contexts/FuriganaContext';
import { logClientError, logClientWarn } from '../utils/safeLog';
import { DESKTOP_LAYOUT_MIN_VIEWPORT } from '../utils/tabletLayoutThresholds';
import TeacherToolbar from './TeacherToolbar';
import TeacherPresetPanel from './TeacherPresetPanel';
import { useTeacherPresets } from '../hooks/useTeacherPresets';
import { mergePresetIntoProfile } from '../utils/profileAdapter';
import { getVerificationBadgeContent } from '../utils/verificationBadge';
import { sessionAPI, authAPI, problemsAPI, studentAPI } from '../utils/api';
import api from '../utils/api';
import { logout } from '../utils/auth';
import { normalizeFontFamily, resolveFontFamily } from '../utils/fontFamilyResolver';

// UI action types that reference graph elements and therefore need the final
// stage visible before overlays apply (shared by chat batch + voice paths).
const ELEMENT_TARGETING_TYPES = ['highlight_elements', 'focus_single_element', 'dim_irrelevant', 'show_value_tags'];
// Safety cap for the voice-path overlay accumulator.
const VOICE_OVERLAY_MAX_ACTIONS = 16;

// i18n: grade_level / role are stored as canonical Japanese DB values (中学2年 / 教師);
// map to a localized label key (Bug #4 — avatar badge showed the raw JA value in EN mode).
const GRADE_VALUE_TO_LABEL_KEY = {
  '中学1年': 'register.gradeJhs1',
  '中学2年': 'register.gradeJhs2',
  '中学3年': 'register.gradeJhs3',
  '教師': 'register.summaryTeacher',
};

function Dashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const fa = useFuriganaAria();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [settings, setSettings] = useState({
    fontFamily: "'UDデジタル教科書体', sans-serif",
    fontSize: 16,
    letterSpacing: 0.05,
    textColor: '#000000',
    voiceInputEnabled: true,
    voiceOutputEnabled: true,
    voiceType: 'female_neural',
    speechRate: 1.0,
    speechVolume: 1.0,
    highlightAccentColor: '#facc15',
    problemTextHighlightColor: '#facc15',
    colorAccessibilityPreset: 'auto',
    attentionAnimationSpeed: 1.0,
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [integrationPayload, setIntegrationPayload] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [graphTypeOverrides, setGraphTypeOverrides] = useState({});
  const containerRef = useRef(null);
  const layoutInfoRef = useRef({ containerWidth: 0 });
  const defaultGraphRatio = 0.6;
  const [graphPanelWidth, setGraphPanelWidth] = useState(() => Math.round((window.innerWidth - 320) * defaultGraphRatio));
  const graphWidthRef = useRef(graphPanelWidth);
  const minGraphWidth = 350;
  const minChatWidth = 260;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(window.innerWidth < DESKTOP_LAYOUT_MIN_VIEWPORT);
  // compact lesson-list is a full-viewport screen: focus its close button on open, return focus to the
  // hamburger on close (declared here so the open/close handlers + focus effect share them).
  const sidebarCloseRef = useRef(null);
  const sidebarOpenBtnRef = useRef(null);
  const sidebarDialogRef = useRef(null);
  useEffect(() => {
    if (!(isCompact && sidebarOpen)) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setSidebarOpen(false); return; }
      if (e.key !== 'Tab') return;
      // Focus trap: the full-viewport dialog is opaque over the still-mounted dashboard, so keep Tab /
      // Shift+Tab inside it (query focusables at Tab-time so SessionList's dynamic controls are included).
      const dialog = sidebarDialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(dialog.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) { e.preventDefault(); first.focus(); return; } // focus escaped → pull it back
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    const raf = requestAnimationFrame(() => { sidebarCloseRef.current?.focus(); });
    return () => {
      window.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
      sidebarOpenBtnRef.current?.focus();  // return focus to the hamburger that opened it
    };
  }, [isCompact, sidebarOpen]);
  const separatorRef = useRef(null);
  const isDraggingRef = useRef(false);
  const userDraggedRef = useRef(false);

  // Teacher mode
  const isTeacherMode = currentUser?.role === 'teacher';
  const {
    presets, selectedPresetId, setSelectedPresetId,
    selectedPreset, loadPresets,
    isPresetPanelOpen, setIsPresetPanelOpen,
  } = useTeacherPresets(isTeacherMode);

  // Class join state
  const [myTeacher, setMyTeacher] = useState(undefined); // undefined=loading, null=no teacher, object=has teacher
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [conflictInfo, setConflictInfo] = useState(null); // { current_teacher, new_teacher_name, code }

  // Phase 2: chat-to-overlay coordination
  const [dashboardUiActions, setDashboardUiActions] = useState(null);
  // Part 2 PR1: the renderer's screen-identity receipt, lifted here so the
  // Dashboard chat mount is grounded exactly like the ProblemIntegration one.
  // Wiring only PIV left this production path with no grounding at all.
  const [renderState, setRenderState] = useState(null);
  const [focusElementId, setFocusElementId] = useState(null);
  // Voice tool calls arrive ONE action per call (realtimeUiActionExecutor's
  // POST-then-apply contract emits a single-action array per 200 response).
  // They must accumulate here: plain replace semantics would leave only the
  // last action visible (highlights wiping each other, one element remaining).
  const voiceOverlayActionsRef = useRef([]);

  const triggerFinalStageJumpIfTargeting = useCallback((actions) => {
    // Auto-switch to final stage when AI returns element-targeting actions
    const hasElementTargeting = Array.isArray(actions) &&
      actions.some((a) => ELEMENT_TARGETING_TYPES.includes(a.type));
    if (hasElementTargeting) {
      setFocusElementId(null);
      requestAnimationFrame(() => {
        setFocusElementId(`__trigger_last_stage__::${Date.now()}`);
        setTimeout(() => setFocusElementId(null), 800);
      });
    }
  }, []);

  // Chat path: whole-batch replace (PACE responses carry the full action group).
  const handleDashboardUiActions = useCallback((actions) => {
    voiceOverlayActionsRef.current = []; // a new chat batch supersedes voice accumulation
    setDashboardUiActions(actions);
    triggerFinalStageJumpIfTargeting(actions);
  }, [triggerFinalStageJumpIfTargeting]);

  // Voice path: accumulate + dedup (same type over the same element set
  // replaces its previous occurrence) so sequential tool calls compose.
  const handleVoiceOverlayAction = useCallback((actions) => {
    const incoming = Array.isArray(actions) ? actions : [];
    const keyOf = (action) => `${action?.type}::${(Array.isArray(action?.element_ids) ? [...action.element_ids].sort() : []).join(',')}`;
    const merged = [...voiceOverlayActionsRef.current];
    incoming.forEach((action) => {
      const key = keyOf(action);
      const existingIndex = merged.findIndex((prev) => keyOf(prev) === key);
      if (existingIndex >= 0) {
        merged[existingIndex] = action;
      } else {
        merged.push(action);
      }
    });
    while (merged.length > VOICE_OVERLAY_MAX_ACTIONS) merged.shift();
    voiceOverlayActionsRef.current = merged;
    setDashboardUiActions(merged);
    triggerFinalStageJumpIfTargeting(incoming);
  }, [triggerFinalStageJumpIfTargeting]);

  // Overlay state is scoped to the current session's graph: clear both the
  // voice accumulator AND the applied actions on session switch, so stale
  // actions (incl. one-shot set_stage) never survive into another session or
  // re-execute when InteractiveGraph remounts with a fresh consume-once set.
  useEffect(() => {
    voiceOverlayActionsRef.current = [];
    setDashboardUiActions(null);
  }, [currentSession?.id]);

  const clamp = useCallback((value, min, max) => Math.min(Math.max(value, min), max), []);

  const getContainerWidth = useCallback(() => {
    return layoutInfoRef.current.containerWidth || containerRef.current?.clientWidth || (graphWidthRef.current + minChatWidth);
  }, [minChatWidth]);

  const getMaxGraphWidth = useCallback(() => {
    const containerWidth = getContainerWidth();
    const gap = layoutInfoRef.current.gap ?? 24;
    const separatorWidth = layoutInfoRef.current.separatorWidth ?? 16;
    const reservedWidth = minChatWidth + separatorWidth + gap * 2;
    return Math.max(containerWidth - reservedWidth, minGraphWidth);
  }, [getContainerWidth, minChatWidth, minGraphWidth]);

  const resetGraphWidth = useCallback(() => {
    const maxGraphWidth = getMaxGraphWidth();
    const containerWidth = getContainerWidth();
    const defaultWidth = clamp(containerWidth * defaultGraphRatio, minGraphWidth, maxGraphWidth);
    graphWidthRef.current = defaultWidth;
    userDraggedRef.current = false;
    setGraphPanelWidth(defaultWidth);
  }, [clamp, getContainerWidth, getMaxGraphWidth, minGraphWidth]);

  // 加载用户信息和会话列表
  const [cognitiveProfile, setCognitiveProfile] = useState(null);

  const loadUserData = useCallback(async () => {
    try {
      const [userResponse, settingsResponse] = await Promise.all([
        authAPI.getCurrentUser(),
        authAPI.getSettings(),
      ]);

      setCurrentUser(userResponse.data);
      // Canonicalize the server value (legacy English DB default → logical name)
      // so state, the SettingsPanel <select>, and the save API all agree.
      setSettings((prev) => {
        const merged = { ...prev, ...settingsResponse.data };
        return { ...merged, fontFamily: normalizeFontFamily(merged.fontFamily) };
      });

      // Fetch cognitive difficulty profile (used for overlay computation)
      try {
        const profileResponse = await api.get('/api/profile/complete');
        if (profileResponse.data?.cognitive_difficulty) {
          setCognitiveProfile(profileResponse.data.cognitive_difficulty);
        }
      } catch (profileErr) {
        // Graceful fallback: cognitive profile is optional
        logClientWarn('Could not load cognitive profile:', profileErr);
      }
    } catch (error) {
      logClientError('Failed to load user data:', error);
    }
  }, []);

  // Load teacher association
  const loadMyTeacher = useCallback(async () => {
    try {
      const res = await studentAPI.getMyTeacher();
      setMyTeacher(res.data?.teacher || null);
    } catch (err) {
      logClientError('Failed to load teacher info:', err);
      setMyTeacher(null);
    }
  }, []);

  const handleJoinClass = async (code, force = false) => {
    setJoinLoading(true);
    setJoinError('');
    try {
      const res = await studentAPI.joinClass(code || joinCode, force);
      if (res.data?.conflict) {
        setConflictInfo({
          current_teacher: res.data.current_teacher,
          new_teacher_name: res.data.teacher_name || t('dashboard.newTeacherFallback'),
          code: code || joinCode,
        });
      } else {
        setConflictInfo(null);
        setJoinCode('');
        await loadMyTeacher();
      }
    } catch (err) {
      logClientError('Failed to join class:', err);
      // SessionList shows a localized generic join error, so we only need a truthy
      // flag here (the backend `detail` is Japanese-only; error codes are Phase 7).
      setJoinError('join_failed');
    } finally {
      setJoinLoading(false);
    }
  };

  const handleLeaveClass = async () => {
    if (!window.confirm(t('dashboard.leaveClassConfirm', { teacher: myTeacher?.username || t('dashboard.teacherFallback') }))) return;
    try {
      await studentAPI.leaveClass();
      setMyTeacher(null);
    } catch (err) {
      logClientError('Failed to leave class:', err);
    }
  };

  const loadIntegrationForSession = useCallback(async (sessionData) => {
    if (!sessionData?.id) {
      setIntegrationPayload(null);
      return;
    }

    try {
      const response = await problemsAPI.getBySession(sessionData.id);
      const payload = response.data;
      if (payload?.problem_id && payload?.problem_data) {
        setIntegrationPayload({
          problem_id: payload.problem_id,
          graph_data: sessionData.graph_data,
          problem_data: payload.problem_data,
          // Phase A: present only when the server's instrumentation flag is on for this student.
          research_task: payload.research_task || null,
        });
        return;
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        logClientError('Failed to load integration data:', error);
      }
    }

    setIntegrationPayload(null);
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    try {
      const response = await sessionAPI.get(sessionId);
      setCurrentSession(response.data);
      setShowUploader(false);
      await loadIntegrationForSession(response.data);
    } catch (error) {
      logClientError('Failed to load session:', error);
    }
  }, [loadIntegrationForSession]);

  const currentSessionRef = useRef(currentSession);
  currentSessionRef.current = currentSession;
  const showUploaderRef = useRef(showUploader);
  showUploaderRef.current = showUploader;
  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await sessionAPI.getAll();
      setSessions(response.data);

      // 如果有会话，自动选择最新的
      if (response.data.length > 0 && !currentSessionRef.current && !showUploaderRef.current) {
        loadSession(response.data[0].id);
      }
    } catch (error) {
      logClientError('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [loadSession]);

  useEffect(() => {
    loadUserData();
    loadSessions();
    loadMyTeacher();
  }, [loadSessions, loadUserData, loadMyTeacher]);

  const handleNewSession = () => {
    setCurrentSession(null);
    setShowUploader(true);
    setIntegrationPayload(null);
  };

  const handleGraphUploaded = async (uploadPayload) => {
    try {
      const graphData = uploadPayload?.graph_data || uploadPayload;
      const graphId = uploadPayload?.graph_id || graphData?.graph_id;
      const problemDetected = Boolean(uploadPayload?.problem_detected);
      const problemId = uploadPayload?.problem_id;
      const stages = Array.isArray(uploadPayload?.stages)
        ? uploadPayload.stages
        : (Array.isArray(graphData?.stages) ? graphData.stages : []);
      // Persist initial_overlays from backend response (Phase 1 cognitive adaptation)
      const initialOverlays = Array.isArray(uploadPayload?.initial_overlays)
        ? uploadPayload.initial_overlays
        : [];

      const graphDataWithStages = {
        ...graphData,
        graph_id: graphId || graphData?.graph_id,
        stages,
        total_stages: graphData?.total_stages || stages.length || graphData?.stages?.length || 0,
        first_stage: graphData?.first_stage || stages[0] || null,
        current_stage: graphData?.current_stage ?? 0,
        initial_overlays: initialOverlays.length > 0 ? initialOverlays : undefined,
      };

      const graphTypeName = t(`graphType.${graphData.graph_type}`, { defaultValue: t('graphType.generic') });
      const defaultTitle = `${graphTypeName} - ${new Date().toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')}`;

      // 创建新会话
      const sessionData = {
        title: graphDataWithStages.title || defaultTitle,
        graph_id: graphId,
        graph_type: graphDataWithStages.graph_type,
        graph_data: graphDataWithStages,
      };

      const response = await sessionAPI.create(sessionData);
      const newSession = response.data;

      // 重新加载会话列表
      await loadSessions();
      await loadSession(newSession.id);

      if (problemDetected && problemId) {
        try {
          await problemsAPI.attachSession(problemId, {
            session_id: newSession.id,
            graph_id: graphId,
          });
        } catch (error) {
          logClientError('Failed to attach problem session:', error);
        }
        setIntegrationPayload({
          problem_id: problemId,
          graph_data: graphDataWithStages,  // includes initial_overlays if present
          problem_data: uploadPayload?.problem_data || {},
          // Phase A: present only when the server's instrumentation flag is on for this student.
          research_task: uploadPayload?.research_task || null,
        });
      } else {
        setIntegrationPayload(null);
      }
    } catch (error) {
      logClientError('Failed to create session:', error);
      alert(t('dashboard.createSessionFailed'));
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await sessionAPI.delete(sessionId);

      // 如果删除的是当前会话，清空显示
      if (currentSession && currentSession.id === sessionId) {
        setCurrentSession(null);
        setShowUploader(false);
      }

      // 重新加载会话列表
      await loadSessions();
    } catch (error) {
      logClientError('Failed to delete session:', error);
      alert(t('dashboard.deleteSessionFailed'));
    }
  };

  const handleLogout = () => {
    if (window.confirm(t('dashboard.logoutConfirm'))) {
      logout();
    }
  };

  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < DESKTOP_LAYOUT_MIN_VIEWPORT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    graphWidthRef.current = graphPanelWidth;
  }, [graphPanelWidth]);

  useEffect(() => {
    // The container only exists in the plain-session branch: not while
    // loading, not under the uploader, not when the problem view (PIV) has
    // taken over. Each of those must be a dependency, or the effect runs
    // while the container is still absent, returns early, and never attaches
    // — after which a resize (a window drag, an iPad rotating inside the
    // desktop layout) no longer re-fits the chart. Measured before this
    // guard: 1 in 3 loads ended up with no observer at all.
    if (!currentSession || showUploader || loading || integrationPayload) return;

    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.contentRect.width;
      const previous = layoutInfoRef.current.containerWidth;
      layoutInfoRef.current.containerWidth = width;
      if (element) {
        const styles = window.getComputedStyle(element);
        const columnGap = parseFloat(styles.columnGap || styles.gap || '0');
        if (!Number.isNaN(columnGap)) {
          layoutInfoRef.current.gap = columnGap;
        }
      }
      if (separatorRef.current) {
        layoutInfoRef.current.separatorWidth = separatorRef.current.offsetWidth;
      }

      // Skip width clamping during drag to avoid fighting with the drag handler
      if (isDraggingRef.current) return;

      // Skip if container hasn't fully laid out yet (race condition guard)
      if (width < minGraphWidth + minChatWidth) return;

      const maxGraphWidth = getMaxGraphWidth();
      let nextWidth;

      if (userDraggedRef.current) {
        // User manually adjusted — preserve their choice, just clamp to bounds
        nextWidth = clamp(graphWidthRef.current, minGraphWidth, maxGraphWidth);
      } else {
        // Auto-calculate: always use defaultGraphRatio
        nextWidth = clamp(width * defaultGraphRatio, minGraphWidth, maxGraphWidth);
      }

      if (Math.abs(nextWidth - graphWidthRef.current) > 1) {
        setGraphPanelWidth(nextWidth);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [clamp, currentSession, showUploader, loading, integrationPayload, minChatWidth, minGraphWidth, getMaxGraphWidth]);

  useEffect(() => {
    if (separatorRef.current) {
      layoutInfoRef.current.separatorWidth = separatorRef.current.offsetWidth;
    }
  }, [graphPanelWidth, currentSession]);

  // 创建学生资料对象（兼容现有组件）
  const studentProfile = useMemo(() => {
    if (!currentUser) {
      return null;
    }
    const base = {
      student_id: String(currentUser.id),
      grade_level: currentUser.grade_level,
      difficulty_types: currentUser.difficulty_types,
      preferred_learning_style: currentUser.preferred_learning_style,
      name: currentUser.username,
      username: currentUser.username,
      cognitive_difficulty: cognitiveProfile || null,
    };
    // 教師モード: プリセット選択中はマージ
    if (isTeacherMode && selectedPreset) {
      return mergePresetIntoProfile(base, selectedPreset);
    }
    return base;
  }, [currentUser, cognitiveProfile, isTeacherMode, selectedPreset]);

  const currentGraphId = currentSession?.graph_data?.graph_id;
  const autoDetectedGraphType = currentSession?.graph_data?.graph_type;
  const overriddenGraphType = currentGraphId ? graphTypeOverrides[currentGraphId] : null;
  const effectiveGraphType = overriddenGraphType || autoDetectedGraphType;
  const verificationStatus = currentSession?.graph_data?.verification_status;
  const effectiveGraphData = useMemo(() => {
    if (!currentSession?.graph_data) return null;
    if (!effectiveGraphType || currentSession.graph_data.graph_type === effectiveGraphType) {
      return currentSession.graph_data;
    }
    return {
      ...currentSession.graph_data,
      graph_type: effectiveGraphType
    };
  }, [currentSession, effectiveGraphType]);

  const GRAPH_TYPE_OPTIONS = [
    // 統計系グラフ
    { value: 'bar_chart', label: t('graphType.bar_chart') },
    { value: 'line_chart', label: t('graphType.line_chart') },
    { value: 'pie_chart', label: t('graphType.pie_chart') },
    { value: 'scatter_plot', label: t('graphType.scatter_plot') },
    { value: 'histogram', label: t('graphType.histogram') },
    { value: 'box_plot', label: t('graphType.box_plot') },
    { value: 'band_graph', label: t('graphType.band_graph') },
    { value: 'dot_plot', label: t('graphType.dot_plot') },
    { value: 'venn_diagram', label: t('graphType.venn_diagram') },
    { value: 'tree_diagram', label: t('graphType.tree_diagram') },
    { value: 'area_chart', label: t('graphType.area_chart') },
    { value: 'stacked_bar_chart', label: t('graphType.stacked_bar_chart') },
    // 関数系グラフ
    { value: 'linear_function', label: t('graphType.linear_function') },
    { value: 'quadratic_function', label: t('graphType.quadratic_function') },
    { value: 'trigonometric_function', label: t('graphType.trigonometric_function') },
    { value: 'exponential_function', label: t('graphType.exponential_function') },
    { value: 'logarithmic_function', label: t('graphType.logarithmic_function') },
    { value: 'inverse_proportion', label: t('graphType.inverse_proportion') },
    { value: 'proportion', label: t('graphType.proportion') }
  ];

  const handleGraphTypeOverrideChange = (event) => {
    const nextType = event.target.value;
    if (!currentGraphId) {
      return;
    }
    setGraphTypeOverrides((prev) => {
      const nextOverrides = { ...prev };
      if (!nextType || nextType === autoDetectedGraphType) {
        delete nextOverrides[currentGraphId];
      } else {
        nextOverrides[currentGraphId] = nextType;
      }
      return nextOverrides;
    });
  };

  return (
    <div
      className="min-h-screen px-1 sm:px-2 lg:px-3 py-2 lg:py-3 text-[var(--text)]"
      style={{ fontFamily: resolveFontFamily(settings.fontFamily) }}
    >
      {/* ── Compact sidebar overlay ── */}
      {isCompact && sidebarOpen && (
        // Compact/tablet: the lesson list opens as a TRUE full-viewport screen (opaque, not a narrow
        // w-72 side drawer), so it works on iPad portrait AND landscape. It overlays the chat body
        // (a fixed layer over the flex-1 flow child) without hiding it — closing restores the chat.
        // Safe area: the app keeps the DEFAULT viewport-fit (contain), so the viewport already excludes
        // the notch / home-indicator and inset-0 fills the SAFE area; the env(safe-area-inset-*) padding
        // below is defensive (a no-op today, correct if the shell ever adopts viewport-fit=cover).
        <div
          ref={sidebarDialogRef}
          className="fixed inset-0 z-[60] flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={t('dashboard.lessonListTitle')}
          style={{
            background: 'var(--bg-surface)',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          }}
        >
          <div className="flex items-center justify-between px-3 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
            <span className="text-base font-bold" style={{ color: 'var(--text)' }}><FuriganaText text={t('dashboard.lessonListTitle')} /></span>
            <button
              ref={sidebarCloseRef}
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label={t('dashboard.closeLessonList')}
              className="flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ minHeight: 44, minWidth: 44, background: 'var(--bg-muted)', color: 'var(--text)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 py-2">
            <div className="mx-auto w-full max-w-md">
              <SessionList
                sessions={sessions}
                currentSessionId={currentSession?.id}
                onSelectSession={(id) => { loadSession(id); setSidebarOpen(false); }}
                onNewSession={() => { handleNewSession(); setSidebarOpen(false); }}
                onDeleteSession={handleDeleteSession}
                myTeacher={myTeacher}
                joinCode={joinCode}
                setJoinCode={setJoinCode}
                joinLoading={joinLoading}
                joinError={joinError}
                conflictInfo={conflictInfo}
                setConflictInfo={setConflictInfo}
                onJoinClass={handleJoinClass}
                onLeaveClass={handleLeaveClass}
                isTeacherMode={isTeacherMode}
              />
            </div>
          </div>
        </div>
      )}

      <div className={`w-full mx-auto flex gap-2 px-1 min-h-[calc(100vh-1.5rem)] ${isCompact ? 'flex-col' : 'flex-row items-stretch gap-4 px-3 h-[calc(100vh-3rem)] max-h-[calc(100vh-3rem)]'}`}>
        {/* Sidebar - only inline on large screens */}
        {!isCompact && (
          <SessionList
            sessions={sessions}
            currentSessionId={currentSession?.id}
            onSelectSession={loadSession}
            onNewSession={handleNewSession}
            onDeleteSession={handleDeleteSession}
            myTeacher={myTeacher}
            joinCode={joinCode}
            setJoinCode={setJoinCode}
            joinLoading={joinLoading}
            joinError={joinError}
            conflictInfo={conflictInfo}
            setConflictInfo={setConflictInfo}
            onJoinClass={handleJoinClass}
            onLeaveClass={handleLeaveClass}
            isTeacherMode={isTeacherMode}
          />
        )}

          <div className={`flex-1 flex flex-col border overflow-hidden min-h-0 min-w-0 ${isCompact ? 'rounded-[16px]' : 'rounded-[20px]'}`} style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-lg)' }}>
          <header className={`relative overflow-visible ${isCompact ? 'px-3 py-3' : 'px-5 py-3'}`} style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}>
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ background: 'radial-gradient(circle at top left, #ffffff 0%, transparent 55%)' }} />
            <div className="relative flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                {isCompact && (
                  <button
                    ref={sidebarOpenBtnRef}
                    onClick={() => setSidebarOpen(true)}
                    className="flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--text-on-primary)' }}
                    aria-label={t('dashboard.openLessonList')}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                  </button>
                )}
                <div>
                <p className="uppercase tracking-[0.4em] text-[9px] font-semibold" style={{ color: 'rgba(253,251,246,0.9)' }}>
                  Graph Tutor AI
                </p>
                <h1 className="mt-1 text-sm lg:text-base font-bold">
                  <FuriganaText text={t('dashboard.title')} />
                </h1>
                <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10px] font-medium" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <span className="text-sm">📚</span>
                  <span><FuriganaText text={currentSession?.title || t('dashboard.thisWeeksStudy')} /></span>
                </div>
              </div>
              </div>

              {isTeacherMode && (
                <TeacherToolbar
                  presets={presets}
                  selectedPresetId={selectedPresetId}
                  onPresetChange={setSelectedPresetId}
                  onOpenPresetPanel={() => setIsPresetPanelOpen(true)}
                  currentUser={currentUser}
                />
              )}

              <div className="relative ml-auto z-50">
                <button
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-semibold transition-all" style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  {/* Decorative first-letter avatar glyph — redundant with the full
                      username shown next to it (the unverified name stays bare). aria-hidden so it is
                      not read by SR and is not participant reading-content: a single-kanji
                      monogram has no reliable reading (田 → た vs でん), so per
                      correctness-first it is left un-annotated rather than mis-read. */}
                  <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-lg font-bold" style={{ background: 'rgba(255,255,255,0.7)', color: 'var(--primary-strong)' }}>
                    {currentUser?.username?.slice(0, 1)?.toUpperCase() || 'Y'}
                  </span>
                  <div className="text-left">
                    {/* unverified proper name → bare (never-guess), Codex R19 #2; the fallback is a plain word */}
                    <div className="font-semibold" style={{ color: 'var(--text-on-primary)' }}>{currentUser?.username || t('dashboard.userFallback')}</div>
                    <div className="text-xs" style={{ color: 'rgba(253,251,246,0.8)' }}><FuriganaText text={currentUser?.grade_level ? (GRADE_VALUE_TO_LABEL_KEY[currentUser.grade_level] ? t(GRADE_VALUE_TO_LABEL_KEY[currentUser.grade_level]) : currentUser.grade_level) : t('dashboard.gradeFallback')} /></div>
                  </div>
                  <span className="text-sm" style={{ color: 'rgba(253,251,246,0.8)' }}>▾</span>
                </button>
	                  {isUserMenuOpen && (
	                    <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg overflow-visible z-50" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                    <button
                      onClick={() => { setIsSettingsOpen(true); setIsUserMenuOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-xs hover:bg-[var(--bg-muted)]" style={{ color: 'var(--text)' }}
                    >
                      <FuriganaText>⚙️ {t('settings.title')}</FuriganaText>
                    </button>
                    <button
                      onClick={() => { navigate('/profile'); setIsUserMenuOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-xs hover:bg-[var(--bg-muted)]" style={{ color: 'var(--text)' }}
                    >
                      🧑‍🏫 {t('dashboard.menuProfile')}
                    </button>
                    <button
                      onClick={() => { resetGraphWidth(); setIsUserMenuOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-xs hover:bg-[var(--bg-muted)]" style={{ color: 'var(--text)' }}
                    >
                      <FuriganaText>♻️ {t('dashboard.menuResetWidth')}</FuriganaText>
                    </button>
                    {isTeacherMode && (
                      <button
                        onClick={() => { navigate('/teacher/dashboard'); setIsUserMenuOpen(false); }}
                        className="flex w-full items-center gap-2 px-4 py-3 text-xs hover:bg-[var(--bg-muted)]" style={{ color: 'var(--text)' }}
                      >
                        <FuriganaText text={t('dashboard.menuAnalytics')} />
                      </button>
                    )}
                    <button
                      onClick={() => { handleLogout(); setIsUserMenuOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-xs hover:bg-[var(--danger-bg)]" style={{ color: 'var(--danger)', borderTop: '1px solid var(--border)' }}
                    >
                      🚪 {t('dashboard.menuLogout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <main className="flex-1 min-h-0 p-4 lg:p-6 flex flex-col overflow-hidden" style={{ background: 'var(--bg-base)' }}>
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="mb-4 text-6xl animate-pulse-slow">⏳</div>
                  <p className="text-[var(--text-muted)]"><FuriganaText text={t('dashboard.preparing')} /></p>
                </div>
              </div>
            ) : showUploader ? (
              <div className="flex-1 min-h-0 overflow-auto pr-2">
                <div className="mx-auto max-w-4xl">
                  <GraphUploader
                    studentId={String(currentUser?.id)}
                    onGraphUploaded={handleGraphUploaded}
                  />
                </div>
              </div>
            ) : integrationPayload ? (
              <div className="flex-1 min-h-0 overflow-auto pr-2 min-[800px]:max-h-[calc(100vh-9rem)]">
                <ProblemIntegrationView
                  problemId={integrationPayload.problem_id}
                  sessionId={currentSession?.id}
                  graphId={currentSession?.graph_id}
                  graphData={integrationPayload.graph_data}
                  problemData={integrationPayload.problem_data}
                  researchTask={integrationPayload.research_task || null}
                  studentProfile={studentProfile}
                  settings={settings}
                  onExit={() => setIntegrationPayload(null)}
                />
              </div>
            ) : currentSession ? (
              <div className="flex-1 min-h-0">
                <div
                  ref={containerRef}
                  className={`relative flex flex-row min-h-0 w-full gap-2 rounded-xl p-2 lg:p-3 overflow-hidden h-full`}
                  style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)', alignItems: 'stretch' }}
                >
                  <div
                    style={isCompact
                      ? { flex: 3, minWidth: 0, background: 'var(--bg-surface)', border: '1px solid var(--border)' }
                      : { width: graphPanelWidth, minWidth: minGraphWidth, transition: 'none' /* animated width = one full chart redraw per frame (redraw-budget.spec.js) */, background: 'var(--bg-surface)', border: '1px solid var(--border)' }
                    }
                    className={`flex flex-col min-h-0 rounded-xl p-2 lg:p-3 overflow-hidden ${isCompact ? '' : 'flex-shrink-0 h-full'}`}
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--text-muted)]">
                      <div>
                        <span className="font-semibold text-[var(--text)]"><FuriganaText text={t('dashboard.aiDetection')} /></span>{' '}
                        <span className="rounded-full bg-[var(--bg-muted)] px-2 py-0.5 font-semibold text-[var(--text)]">
                          <FuriganaText text={autoDetectedGraphType ? GRAPH_TYPE_OPTIONS.find((option) => option.value === autoDetectedGraphType)?.label || autoDetectedGraphType : t('dashboard.notDetected')} />
                        </span>
                        {overriddenGraphType && (
                          <span className="ml-2 rounded-full px-2 py-0.5 font-semibold" style={{ background: 'var(--warm-bg)', color: 'var(--warning)' }}>
                            <FuriganaText text={t('dashboard.overriding')} />
                          </span>
                        )}
                        {(() => {
                          const badge = getVerificationBadgeContent(verificationStatus, isTeacherMode, t);
                          if (!badge.shouldRender) return null;
                          // disagreement 抑制: 学生/教師が手動で graph_type を override 中なら badge を出さない
                          // (helper 外で AND、太子追加条件 B「既存 if-elif chain 順序・条件式・renderer 不変」)
                          if (verificationStatus === 'disagreement' && overriddenGraphType) return null;
                          return (
                            <span
                              className={`ml-2 ${badge.className}`}
                              style={badge.inlineStyle}
                              title={fa(badge.tooltip)}
                              aria-label={badge.ariaLabel}
                            >
                              <FuriganaText text={badge.label} />
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2 text-[13px]">
                        <label htmlFor="graph-type-override" className="font-semibold text-[var(--text)]">
                          <FuriganaText text={t('dashboard.changeGraphType')} />
                        </label>
                        <select
                          id="graph-type-override"
                          value={effectiveGraphType || ''}
                          onChange={handleGraphTypeOverrideChange}
                          className="rounded-lg border px-3 py-1 text-sm text-[var(--text)] focus:outline-none" style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
                        >
                          <option value="">{autoDetectedGraphType ? t('dashboard.backToAi') : t('dashboard.selectGraphType')}</option>
                          {GRAPH_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                      <InteractiveGraph
                        graphData={effectiveGraphData || currentSession?.graph_data}
                        studentProfile={studentProfile}
                        settings={settings}
                        sessionId={currentSession?.id}
                        uiActions={dashboardUiActions}
                        focusElementId={focusElementId}
                        onRenderStateChange={setRenderState}
                      />
                    </div>
                  </div>

                  {!isCompact && <div
                    role="separator"
                    aria-orientation="vertical"
                    className="relative w-3 cursor-col-resize rounded-full z-30" style={{ background: 'var(--border)' }}
                    ref={separatorRef}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      isDraggingRef.current = true;
                      const startX = event.clientX;
                      const initialWidth = graphWidthRef.current;

                      const handleMove = (e) => {
                        const maxGraphWidth = getMaxGraphWidth();
                        const delta = e.clientX - startX;
                        const nextWidth = clamp(initialWidth + delta, minGraphWidth, maxGraphWidth);
                        graphWidthRef.current = nextWidth;
                        setGraphPanelWidth(nextWidth);
                      };

                      const handleUp = () => {
                        isDraggingRef.current = false;
                        userDraggedRef.current = true;
                        window.removeEventListener('pointermove', handleMove);
                      };

                      window.addEventListener('pointermove', handleMove);
                      window.addEventListener('pointerup', handleUp, { once: true });
                      window.addEventListener('pointercancel', handleUp, { once: true });
                    }}
                    onDoubleClick={resetGraphWidth}
                  >
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <span className="text-lg leading-none">⋮</span>
                      <span className="text-xs leading-none">⋮</span>
                    </div>
                  </div>}

                  <div
                    style={isCompact
                      ? { flex: 2, minWidth: 0, background: 'var(--bg-surface)', border: '1px solid var(--border)' }
                      : { flex: 1, minWidth: minChatWidth, background: 'var(--bg-surface)', border: '1px solid var(--border)' }
                    }
                    className="min-h-0 flex flex-col rounded-xl overflow-hidden"
                  >
                    <div className="flex-1 min-h-0">
                      <ChatInterface
                        studentProfile={studentProfile}
                        graphId={currentSession.graph_id}
                        settings={settings}
                        sessionId={currentSession.id}
                        graphType={currentSession.graph_type || currentSession?.graph_data?.graph_type}
                        onUiActions={handleDashboardUiActions}
                        renderState={renderState}
                        presetId={isTeacherMode && selectedPreset ? selectedPreset.id : undefined}
                        supportLevel={isTeacherMode && selectedPreset ? selectedPreset.support_level : undefined}
                      />
                    </div>
                    {/* P0-4 Phase 2: Realtime Voice Tutor — student-only, requires uploaded graph.
                        Mount condition mirrors panel's own internal CRITICAL-2 guard
                        (defense-in-depth: panel re-checks and returns null on mismatch). */}
                    {!isTeacherMode && currentSession?.id && currentSession?.graph_id && effectiveGraphData && (
                      <RealtimeVoiceTutorPanel
                        sessionId={currentSession.id}
                        studentProfile={studentProfile}
                        onApplyOverlay={handleVoiceOverlayAction}
                        isTeacherMode={isTeacherMode}
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="max-w-lg rounded-xl p-10 text-center" style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)', border: '1px solid var(--border)' }}>
                  <div className="mb-4 text-6xl">📊</div>
                  <h2 className="mb-3 text-lg font-bold text-[var(--text)]">
                    <FuriganaText text={t('dashboard.emptyTitle')} />
                  </h2>
                  <p className="mb-6 leading-relaxed text-[var(--text-muted)]">
                    <FuriganaText text={t('dashboard.emptyBody')} />
                  </p>
                  <button
                    onClick={handleNewSession}
                    className="rounded-lg px-6 py-3 font-semibold transition-all" style={{ background: 'var(--primary)', boxShadow: 'var(--shadow-sm)', color: 'var(--text-on-primary)' }}
                  >
                    <FuriganaText>+ {t('dashboard.startNewLesson')}</FuriganaText>
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* AI免責声明 */}
      <div className="px-6 pb-3 pt-1 text-center text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        <FuriganaText text={t('dashboard.aiDisclaimer')} />
      </div>

      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onSettingsChange={async (newSettings) => {
          setSettings(newSettings);
          try {
            await authAPI.updateSettings(newSettings);
          } catch (error) {
            logClientError('Failed to save settings:', error);
          }
        }}
      />

      {isTeacherMode && (
        <TeacherPresetPanel
          isOpen={isPresetPanelOpen}
          onClose={() => setIsPresetPanelOpen(false)}
          onPresetChange={loadPresets}
        />
      )}
    </div>
  );
}

export default Dashboard;
